import { useState } from "react";
import type { Snapshot, Lane, LaneColour } from "../types";
import { STATUS_DOT, avatarUrlFor, emojiFor, emojiDataUrl } from "../lanes";

// War Room v0.5 — server-authoritative colour drives the lane tile shell.
const COLOUR_CLASS: Record<LaneColour, string> = {
  green: "bg-emerald-950 border-emerald-700",
  amber: "bg-amber-950 border-amber-600",
  red: "bg-red-950 border-red-600 animate-pulse",
  unknown: "bg-ink/60 border-edge",
};

// Status glyph next to the lane name. Sentence case, no emoji.
const STATUS_GLYPH: Record<string, string> = {
  working: "●", // ●
  done: "✓",    // ✓
  stuck: "!",
  idle: "·",    // ·
};

type SectionKey = "lanes" | "inbox" | "war" | "todos";

export function RightRail({ snap, onOpenLane }: { snap: Snapshot | null; onOpenLane: (l: Lane) => void }) {
  const [collapsed, setCollapsed] = useState<Record<SectionKey, boolean>>({
    lanes: false, inbox: false, war: false, todos: false,
  });
  const toggle = (k: SectionKey) => setCollapsed((c) => ({ ...c, [k]: !c[k] }));
  return (
    <aside className="w-[340px] shrink-0 border-l border-edge bg-panel/40 overflow-auto scroll-thin">
      <Section title="Lane Wall" count={snap?.fleet.lanes.length} open={!collapsed.lanes} onToggle={() => toggle("lanes")}>
        <LaneWall snap={snap} onOpenLane={onOpenLane} />
      </Section>
      <Section title="Inbox" count={snap?.inbox.length} open={!collapsed.inbox} onToggle={() => toggle("inbox")}>
        <Inbox snap={snap} />
      </Section>
      <Section title="Chatter" count={snap?.war_room.length} open={!collapsed.war} onToggle={() => toggle("war")}>
        <Chatter snap={snap} />
      </Section>
      <Section title="Frankie Todos" count={snap?.frankie_todos.length} open={!collapsed.todos} onToggle={() => toggle("todos")}>
        <Todos snap={snap} />
      </Section>
    </aside>
  );
}

function Section({
  title, count, open, onToggle, children,
}: { title: string; count?: number; open: boolean; onToggle: () => void; children: React.ReactNode }) {
  return (
    <div className="border-b border-edge">
      <button onClick={onToggle} className="w-full flex items-center px-3 py-2 text-xs uppercase tracking-wide text-mist hover:text-white">
        <span>{open ? "▾" : "▸"}</span>
        <span className="ml-2">{title}</span>
        {typeof count === "number" && <span className="ml-auto">{count}</span>}
      </button>
      {open && <div className="px-2 pb-3">{children}</div>}
    </div>
  );
}

function LaneWall({ snap, onOpenLane }: { snap: Snapshot | null; onOpenLane: (l: Lane) => void }) {
  const lanes = snap?.fleet.lanes ?? [];
  if (lanes.length === 0) return <div className="text-mist text-xs px-1">No data yet</div>;
  return (
    <div className="grid grid-cols-2 gap-2">
      {lanes.map((l) => {
        const colour: LaneColour = l.colour ?? "unknown";
        const shell = COLOUR_CLASS[colour];
        const glyph = STATUS_GLYPH[l.status] ?? STATUS_GLYPH.idle;
        // Age is shown only when the lane is not actively working AND has
        // gone past its hot threshold. Server reports age_minutes; client
        // doesn't recompute.
        const showAge =
          l.status !== "working" &&
          typeof l.ageMinutes === "number" &&
          l.ageMinutes > 0;
        const excerpt = l.lastOutputExcerpt || l.last_action_summary || "—";
        const emoji = l.emoji || emojiFor(l.id);
        const avatarSrc = l.avatar_url && l.avatar_url !== "" ? l.avatar_url : avatarUrlFor(l.id);
        return (
          <button
            key={l.id}
            onClick={() => onOpenLane(l)}
            title={excerpt}
            className={`${shell} border rounded p-2 text-left hover:border-mist transition relative`}
          >
            <div className="flex items-center gap-2">
              <span className="relative inline-block w-9 h-9 shrink-0">
                <img
                  src={avatarSrc}
                  alt=""
                  className="w-9 h-9 rounded-full bg-edge object-cover ring-1 ring-edge"
                  onError={(e) => { (e.currentTarget as HTMLImageElement).src = emojiDataUrl(emoji); }}
                />
                {emoji && (
                  <span className="absolute -bottom-0.5 -right-0.5 text-[11px] leading-none bg-ink rounded-full px-0.5 ring-1 ring-edge">
                    {emoji}
                  </span>
                )}
              </span>
              <div className="min-w-0">
                <div className="text-xs truncate flex items-center gap-1">
                  <span className={`w-1.5 h-1.5 rounded-full ${STATUS_DOT[l.status] ?? "bg-mist"}`} />
                  <span className="font-mono text-[11px] leading-none">{glyph}</span>
                  <span>{l.name}</span>
                </div>
                <div className="text-[10px] text-mist truncate">{excerpt}</div>
              </div>
            </div>
            <span className="absolute top-1 right-1 text-[9px] text-mist font-mono">{l.machine}</span>
            {showAge && (
              <span className="absolute bottom-1 right-1 text-[9px] text-mist font-mono">
                {l.ageMinutes}m
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}

function Inbox({ snap }: { snap: Snapshot | null }) {
  const items = snap?.inbox ?? [];
  if (items.length === 0) return <div className="text-mist text-xs px-1">Inbox is clear.</div>;
  return (
    <div className="space-y-2">
      {items.map((i) => (
        <div key={i.id} className="bg-ink/60 border border-edge rounded p-2">
          <div className="text-xs flex items-center gap-2">
            <span className="text-mist">{i.lane}</span>
            <span className="ml-auto text-[10px] text-mist">{i.severity}</span>
          </div>
          <div className="text-sm mt-1">{i.summary}</div>
          <div className="flex gap-1 mt-2 text-xs">
            {(i.actions ?? ["accept", "edit", "ignore", "handback"]).map((a) => (
              <button key={a} className="px-2 py-0.5 border border-edge rounded hover:border-mist capitalize">{a}</button>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function fmtAgo(ts: string): string {
  const mins = Math.round((Date.now() - new Date(ts).getTime()) / 60_000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  return `${Math.round(mins / 60)}h ago`;
}

// Lane names for handoff pickup detection (prefix match).
const PICKUP_PHRASES = ["picked up", "acting on", "sage here", "rex here", "ash here", "knox here", "ralph here", "dingo here", "milo here", "hart here", "benny here", "frankie here", "tess here"];

function isPickup(msg: string): boolean {
  const lower = msg.toLowerCase();
  return PICKUP_PHRASES.some((p) => lower.startsWith(p));
}

function Chatter({ snap }: { snap: Snapshot | null }) {
  const msgs = snap?.war_room ?? [];
  if (msgs.length === 0) return <div className="text-mist text-xs px-1">Quiet.</div>;

  // Build a set of handoff_to values so we can detect pickup links.
  // For each message that is a pickup, find the most recent handoff targeting its lane.
  const handoffLanes = new Set(msgs.filter((m) => m.handoff_to).map((m) => m.handoff_to!));

  return (
    <div className="space-y-1.5">
      {msgs.slice(0, 50).map((m, i) => {
        const isDupe = (m.count ?? 1) > 1;
        const isHandoff = !!m.handoff_to;
        const isChainPickup = isPickup(m.message) && handoffLanes.has(m.lane);

        return (
          <div
            key={i}
            className={[
              "text-xs flex gap-2 rounded px-1 py-0.5",
              isHandoff ? "border-l-2 border-teal-500/70 pl-2" : "",
              isChainPickup ? "border-l-2 border-teal-500/30 pl-2 ml-2" : "",
              isDupe ? "opacity-50" : "",
            ].filter(Boolean).join(" ")}
          >
            <span>{m.emoji}</span>
            <div className="min-w-0 flex-1">
              <div className="text-mist text-[10px] flex items-center gap-1.5">
                {isDupe ? (
                  <span className="text-mist/60">
                    {m.lane} &times; {m.count} <span className="font-mono">(last {fmtAgo(m.ts)})</span>
                  </span>
                ) : (
                  <span>{m.lane}</span>
                )}
                {isHandoff && (
                  <span className="ml-auto text-[10px] font-mono px-1 py-0.5 rounded bg-teal-900/50 text-teal-300 border border-teal-700/40">
                    → {m.handoff_to}
                  </span>
                )}
              </div>
              <div className={["break-words", isDupe ? "text-mist/60" : ""].filter(Boolean).join(" ")}>
                {isChainPickup && <span className="text-teal-400/60 mr-1 select-none">↳</span>}
                {m.message}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function Todos({ snap }: { snap: Snapshot | null }) {
  const todos = snap?.frankie_todos ?? [];
  if (todos.length === 0) return <div className="text-mist text-xs px-1">Nothing on the list.</div>;
  return (
    <div className="space-y-2">
      {todos.map((t) => (
        <div key={t.id} className={`bg-ink/60 border rounded p-2 ${t.urgent ? "border-bad" : "border-edge"}`}>
          <div className="text-xs flex items-center gap-2">
            <span className="text-mist">{t.lane}</span>
            <span className="ml-auto text-[10px] text-mist">{Math.round(t.age_hours)}h</span>
          </div>
          <div className="text-sm mt-1">{t.task}</div>
          <div className="text-[11px] text-mist mt-0.5">{t.action}</div>
          <div className="flex gap-1 mt-2 text-xs">
            {["complete", "snooze", "delegate", "escalate"].map((a) => (
              <button key={a} className="px-2 py-0.5 border border-edge rounded hover:border-mist capitalize">{a}</button>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
